import { useEffect, useMemo, useState } from "react";

import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Shield, Star, Zap, Package, Repeat, MessagesSquare, Trophy, Calendar, Syringe, Reply, MessageSquare, Users, UserPlus, UserMinus, Link as LinkIcon, Pin, PinOff, MapPin, Facebook, Instagram, Youtube, Linkedin, Music2, Twitch, Globe, Ban, ShieldAlert, VolumeX, Volume2 } from "lucide-react";
import { toast } from "sonner";
import FollowersDialog from "./FollowersDialog";
import ActivityHeatmap from "./ActivityHeatmap";
import ReportButton from "./ReportButton";
import BanConfirmDialog from "./BanConfirmDialog";

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const RumbleLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M12 2 L22 12 L12 22 L2 12 Z M9 8 L9 16 L11 16 L11 13 L13 13 L15 16 L17 16 L15 12.5 A2 2 0 0 0 13 8 Z M11 10 L13 10 A0.6 0.6 0 0 1 13 11.4 L11 11.4 Z" />
  </svg>
);

const DiscordLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.443.864-.607 1.25a18.27 18.27 0 0 0-5.486 0 12.6 12.6 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.68 4.37a.07.07 0 0 0-.032.027C.533 9.045-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.042-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.699.772 1.364 1.226 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.947 2.42-2.157 2.42z" />
  </svg>
);

const SOCIAL_ICONS = {
  x: { icon: XLogo, label: "X" },
  facebook: { icon: Facebook, label: "Facebook" },
  instagram: { icon: Instagram, label: "Instagram" },
  youtube: { icon: Youtube, label: "YouTube" },
  tiktok: { icon: Music2, label: "TikTok" },
  linkedin: { icon: Linkedin, label: "LinkedIn" },
  twitch: { icon: Twitch, label: "Twitch" },
  discord: { icon: DiscordLogo, label: "Discord" },
  rumble: { icon: RumbleLogo, label: "Rumble" },
  website: { icon: Globe, label: "Website" },
};

function SocialIconRow({ links }) {
  const entries = Object.entries(links || {}).filter(([k, v]) => SOCIAL_ICONS[k] && v);
  if (entries.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="profile-social-icons">
      {entries.map(([key, url]) => {
        const meta = SOCIAL_ICONS[key];
        const Icon = meta.icon;
        return (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            title={meta.label}
            aria-label={meta.label}
            data-testid={`profile-social-${key}`}
            className="inline-flex items-center justify-center w-7 h-7 border border-slate-200 text-slate-600 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors"
          >
            <Icon className="w-3.5 h-3.5" />
          </a>
        );
      })}
    </div>
  );
}

const bannerFromId = (id = "") => {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const a = h % 360, b = (h * 7) % 360;
  return `linear-gradient(135deg, hsl(${a} 40% 12%) 0%, #0a0a0a 45%, hsl(${b} 55% 16%) 100%)`;
};

const Stat = ({ icon: Icon, label, value, testid, onClick }) => {
  const cls = "border border-slate-200 bg-white px-4 py-3 text-left w-full transition-colors";
  const hover = onClick ? " hover:border-[#0D9488]/60 hover:bg-slate-50/40 cursor-pointer" : "";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className={cls + hover} onClick={onClick} type={onClick ? "button" : undefined} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-500 mb-1">
        <Icon className="w-3 h-3" />
        <span className="font-mono-num text-[10px] uppercase tracking-[0.2em]">{label}</span>
      </div>
      <div className="font-display font-black text-2xl tabular-nums text-slate-900 leading-none">{value}</div>
    </Tag>
  );
};

export default function ProfileHeader({ userId, data: dataProp, onDataLoaded }) {
  const { user: viewer } = useAuth();
  const [data, setData] = useState(dataProp || null);
  const [loading, setLoading] = useState(!dataProp);
  const [followBusy, setFollowBusy] = useState(false);
  const [modal, setModal] = useState(null); // 'followers' | 'following' | null
  const [banDialogOpen, setBanDialogOpen] = useState(false);

  useEffect(() => {
    if (dataProp) { setData(dataProp); setLoading(false); return; }
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${userId}/profile`);
        if (!cancelled) {
          setData(data);
          onDataLoaded?.(data);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, dataProp, onDataLoaded]);

  const banner = useMemo(() => bannerFromId(data?.id), [data?.id]);

  const toggleFollow = async () => {
    if (!data || !viewer) return;
    setFollowBusy(true);
    const currently = !!data.is_following;
    // Optimistic
    setData((cur) => cur ? ({
      ...cur,
      is_following: !currently,
      stats: { ...cur.stats, followers: (cur.stats?.followers || 0) + (currently ? -1 : 1) },
    }) : cur);
    try {
      const key = data.handle || data.id;
      if (currently) {
        await api.delete(`/users/${key}/follow`);
        toast.success(`Unfollowed ${data.name}`);
      } else {
        await api.post(`/users/${key}/follow`);
        toast.success(`Following ${data.name}`);
      }
    } catch (e) {
      // Revert
      setData((cur) => cur ? ({
        ...cur,
        is_following: currently,
        stats: { ...cur.stats, followers: (cur.stats?.followers || 0) + (currently ? 1 : -1) },
      }) : cur);
      toast.error(formatApiError(e));
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) return <div className="border border-slate-200 bg-white p-10 text-slate-500 text-sm animate-pulse">Loading profile…</div>;
  if (!data) return null;
  const initials = (data.name || "?").split(" ").slice(0, 2).map(s => s[0] || "").join("").toUpperCase();
  const showFollowBtn = !!viewer && !data.is_self;
  const viewerIsStaff = viewer?.role === "admin" || viewer?.role === "moderator";
  // Admins can ban users + moderators. Moderators can only ban regular users.
  // Nobody can ban another admin from the UI (must demote first). Never themselves.
  const canBan = viewerIsStaff && !data.is_self && data.role !== "admin" && !(viewer?.role === "moderator" && data.role === "moderator");

  const submitBan = async (reason) => {
    if (!canBan) return;
    const nextBanned = !data.banned;
    const snapshot = { banned: !!data.banned, banned_reason: data.banned_reason || null };
    setData((cur) => cur ? { ...cur, banned: nextBanned, banned_reason: nextBanned ? reason : null } : cur);
    try {
      await api.patch(`/admin/users/${data.id}/ban`, { banned: nextBanned, reason });
      toast.success(nextBanned ? `Banned ${data.name}` : `Unbanned ${data.name}`);
      setBanDialogOpen(false);
    } catch (e) {
      setData((cur) => cur ? { ...cur, ...snapshot } : cur);
      toast.error(formatApiError(e));
    }
  };

  // Manual mute — same permission rules as ban (no self, no admins,
  // moderators can't mute other moderators).
  const canMute = viewerIsStaff && !data.is_self && data.role !== "admin" && !(viewer?.role === "moderator" && data.role === "moderator");
  const isCurrentlyMuted = !!data.muted_until && new Date(data.muted_until).getTime() > Date.now();

  const toggleMute = async () => {
    if (!canMute) return;
    if (isCurrentlyMuted) {
      // Confirm before lifting so nothing gets accidentally cleared.
      if (!window.confirm(`Lift the mute on ${data.name}?`)) return;
      try {
        await api.patch(`/admin/users/${data.id}/mute`, { hours: 0 });
        setData((cur) => cur ? { ...cur, muted_until: null, muted_reason: null } : cur);
        toast.success(`Unmuted ${data.name}`);
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    const hoursStr = window.prompt(`Mute ${data.name} for how many hours? (max 720)`, "24");
    if (hoursStr === null) return;
    const hours = parseInt(hoursStr, 10);
    if (!Number.isFinite(hours) || hours <= 0) { toast.error("Enter a positive number of hours."); return; }
    const reason = window.prompt("Reason (optional, shown to the user)", "") || "";
    try {
      const { data: res } = await api.patch(`/admin/users/${data.id}/mute`, { hours: Math.min(720, hours), reason });
      setData((cur) => cur ? { ...cur, muted_until: res.muted_until, muted_reason: reason || `Manual mute (${hours}h)` } : cur);
      toast.success(`Muted ${data.name} for ${hours}h`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mb-8" data-testid="profile-header">
      {/* Banner — shows the viewer's own Daily Activity heatmap when looking at their own profile,
          otherwise a subtle brand-tinted gradient derived from the user id. */}
      <div className="h-40 sm:h-52 w-full border border-slate-200 relative overflow-hidden" style={data.is_self ? undefined : { background: banner }} data-testid="profile-banner">
        {data.is_self ? (
          <ActivityHeatmap compact />
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(240,185,11,0.15),transparent_60%)]" />
        )}
      </div>

      {/* Avatar + identity */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:gap-6 px-2 sm:px-6 -mt-14 sm:-mt-16 relative">
        <div className="w-28 h-28 sm:w-32 sm:h-32 border-4 border-[#0a0a0a] bg-slate-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="profile-avatar-large">
          {data.avatar_url ? (
            <img src={data.avatar_url} alt={data.name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display font-black text-4xl text-slate-400">{initials}</span>
          )}
        </div>
        <div className="pt-3 sm:pb-2 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight text-slate-900 truncate" data-testid="profile-display-name">{data.name}</h1>
            {data.has_premium && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest text-black bg-[#0D9488] px-2 py-0.5" data-testid="profile-badge-premium"><Star className="w-3 h-3 fill-black" /> Premium</span>
            )}
            {(data.role === "admin" || data.role === "moderator") && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono-num uppercase tracking-widest border border-[#0D9488]/50 text-[#0D9488] px-2 py-0.5" data-testid={`profile-badge-${data.role}`}><Shield className="w-3 h-3" /> {data.role}</span>
            )}
            {data.handle && (
              <span className="font-mono-num text-[11px] text-slate-500 tracking-widest" data-testid="profile-handle">@{data.handle}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] font-mono-num text-slate-500 uppercase tracking-widest flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Joined {data.created_at ? new Date(data.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short" }) : "—"}</span>
            {data.timezone && <span>· {data.timezone}</span>}
          </div>
        </div>
        {showFollowBtn && (
          <div className="pt-3 sm:pb-2 shrink-0 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={toggleFollow}
              disabled={followBusy}
              data-testid={data.is_following ? "profile-unfollow-btn" : "profile-follow-btn"}
              className={`inline-flex items-center gap-2 px-5 h-10 uppercase tracking-widest text-xs font-semibold transition-colors disabled:opacity-50 ${
                data.is_following
                  ? "border border-slate-200 text-slate-800 hover:border-red-500/60 hover:text-red-400 bg-transparent"
                  : "bg-[#0D9488] hover:bg-[#0F766E] text-black"
              }`}
            >
              {data.is_following ? <><UserMinus className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
            </button>
            <ReportButton targetType="user" targetId={data.id} label="Report" />
            {canMute && (
              <button
                type="button"
                onClick={toggleMute}
                data-testid={isCurrentlyMuted ? "profile-unmute-btn" : "profile-mute-btn"}
                title={isCurrentlyMuted ? "Lift the mute" : "Mute this member (pause their posting)"}
                className={`inline-flex items-center gap-2 px-4 h-10 uppercase tracking-widest text-xs font-semibold border transition-colors ${
                  isCurrentlyMuted
                    ? "border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                    : "border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                }`}
              >
                {isCurrentlyMuted ? <><Volume2 className="w-3.5 h-3.5" /> Unmute</> : <><VolumeX className="w-3.5 h-3.5" /> Mute</>}
              </button>
            )}
            {canBan && (
              <button
                type="button"
                onClick={() => setBanDialogOpen(true)}
                data-testid={data.banned ? "profile-unban-btn" : "profile-ban-btn"}
                title={data.banned ? "Unban this member" : "Ban this member"}
                className={`inline-flex items-center gap-2 px-4 h-10 uppercase tracking-widest text-xs font-semibold border transition-colors ${
                  data.banned
                    ? "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                    : "border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                }`}
              >
                {data.banned ? <><ShieldAlert className="w-3.5 h-3.5" /> Unban</> : <><Ban className="w-3.5 h-3.5" /> Ban</>}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Staff-only banned badge — makes it obvious at a glance that this account is suspended. */}
      {data.banned && viewerIsStaff && (
        <div
          className="mt-4 mx-2 sm:mx-6 border border-red-500/40 bg-red-500/5 text-red-400 p-3 flex items-start gap-2"
          data-testid="profile-banned-notice"
        >
          <Ban className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] mb-1">// Account suspended</div>
            <div className="text-slate-800">{data.banned_reason || "No reason provided."}</div>
          </div>
        </div>
      )}
      {isCurrentlyMuted && viewerIsStaff && !data.banned && (
        <div
          className="mt-4 mx-2 sm:mx-6 border border-amber-500/40 bg-amber-50 text-amber-800 p-3 flex items-start gap-2"
          data-testid="profile-muted-notice"
        >
          <VolumeX className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] mb-1">// Muted until {new Date(data.muted_until).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
            <div className="text-slate-800">{data.muted_reason || "Manual mute."}</div>
          </div>
        </div>
      )}

      {/* Bio + link + social icons */}
      {(data.bio || data.link_url || (data.social_links && Object.keys(data.social_links).length > 0)) && (
        <div className="mt-5 px-2 sm:px-6 space-y-2" data-testid="profile-bio-block">
          {data.bio && (
            <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="profile-bio">{data.bio}</div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            {data.link_url && (
              <a
                href={data.link_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 text-xs font-mono-num text-[#0D9488] hover:text-[#0F766E] tracking-widest"
                data-testid="profile-bio-link"
              >
                <LinkIcon className="w-3 h-3" /> {(() => { try { return new URL(data.link_url).host.replace(/^www\./, ""); } catch { return data.link_url; } })()}
              </a>
            )}
            {data.social_links && <SocialIconRow links={data.social_links} />}
          </div>
        </div>
      )}

      {/* Pinned shot — one favourite shot lifted to the top */}
      {data.pinned_shot && (
        <div className="mt-6 mx-2 sm:mx-6 border border-[#0D9488]/40 bg-[#0D9488]/[0.05] p-4" data-testid="profile-pinned-shot">
          <div className="flex items-center gap-2 mb-2">
            <Pin className="w-3.5 h-3.5 text-[#0D9488]" />
            <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488]">// Pinned shot</span>
            <span className="ml-auto font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">{timeAgo(data.pinned_shot.taken_at)}</span>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-display font-black uppercase tracking-tight text-xl text-slate-900 flex items-center gap-2">
              <Syringe className="w-4 h-4 text-[#0D9488]" /> {data.pinned_shot.peptide_name}
            </span>
            <span className="font-mono-num text-slate-800 tabular-nums text-lg">
              {data.pinned_shot.dose_amount ?? "?"} {data.pinned_shot.dose_unit || ""}
            </span>
            {data.pinned_shot.injection_site && (
              <span className="flex items-center gap-1 font-mono-num text-[11px] text-slate-500 uppercase tracking-widest">
                <MapPin className="w-3 h-3" /> {data.pinned_shot.injection_site}
              </span>
            )}
          </div>
          {data.pinned_shot.notes && data.is_self && (
            <div className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{data.pinned_shot.notes}</div>
          )}
        </div>
      )}

      {/* Level bar */}
      <div className="mt-6 mx-2 sm:mx-6 border border-[#0D9488]/30 bg-[#0D9488]/[0.04] p-4" data-testid="profile-level-block">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#0D9488]" />
            <span className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488]">// Level {data.level.level}</span>
          </div>
          <span className="font-mono-num text-[11px] text-slate-500 tabular-nums" data-testid="profile-level-points">{data.level.points} pts · next @ {data.level.next_level_at}</span>
        </div>
        <div className="h-1.5 bg-slate-50 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[#0D9488] transition-all duration-500" style={{ width: `${data.level.progress_pct}%` }} data-testid="profile-level-progress" />
        </div>
      </div>

      {/* Stats row */}
      <div className="mt-4 mx-2 sm:mx-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="profile-stats">
        <Stat icon={Users} label="Followers" value={data.stats.followers ?? 0} testid="stat-followers" />
        <Stat icon={Users} label="Following" value={data.stats.following ?? 0} testid="stat-following" />
        <Stat icon={Syringe} label="Shots" value={data.stats.shots} testid="stat-shots" />
        <Stat icon={Zap} label="Days Active" value={data.stats.days_active} testid="stat-days-active" />
        <Stat icon={Package} label="Vials" value={data.stats.vials} testid="stat-vials" />
        <Stat icon={Repeat} label="Cycles" value={data.stats.cycles} testid="stat-cycles" />
        <Stat icon={MessagesSquare} label="Topics" value={data.stats.topics} testid="stat-topics" />
        <Stat icon={Reply} label="Replies" value={data.stats.replies} testid="stat-replies" />
      </div>

      {canBan && (
        <BanConfirmDialog
          user={data}
          nextBanned={!data.banned}
          open={banDialogOpen}
          onOpenChange={setBanDialogOpen}
          onConfirm={submitBan}
        />
      )}

    </div>
  );
}
